import { create } from 'zustand';
import {
  FilterSubmissionsFieldEnum,
  FilterSubmissionsOrderEnum,
  FilterContractsFieldEnum,
  FilterContractsOrderEnum,
  FilterClaimsFieldEnum,
  FilterClaimsOrderEnum,
} from 'libs/pinnacle-sdk';
import { EBinderContractTab } from 'components/features/binder-contract-table/dashboard-tabs/types';

export interface SelectOption {
  label: string;
  value: string;
}

// Binder Table Filters
export interface BinderTableFilters {
  searchText?: string;
}

// Submission Table Filters (Underwriting Dashboard)
export interface SubmissionTableFilters {
  insureds?: SelectOption[];
  transactionTypes?: string[];
  assignedHandlerIds?: string[];
  submissionStatuses?: string[];
  sortField?: FilterSubmissionsFieldEnum;
  sortOrder?: FilterSubmissionsOrderEnum;
  isFilterOpen: boolean;
}

// Premium Due Table Filters (Finance Dashboard)
export interface PremiumDueTableFilters {
  selectedBrokers?: string[];
  selectedReinsureds?: string[];
  selectedInsureds?: string[];
  selectedBinders?: string[];
  selectedPremiumStatuses?: string[];
  selectedPremiumDueDateRange?: [Date, Date];
  selectedClosing?: boolean;
  sortField?: string;
  sortOrder?: string;
  isFilterOpen: boolean;
}

// Binder Contract Table Filters
export interface BinderContractTableFilters {
  searchPinnacleRefNo?: string;
  searchLegacyPinRefNo?: string;
  selectedInsureds?: string[];
  selectedReinsureds?: string[];
  selectedBrokers?: string[];
  selectedBinders?: string[];
  selectedLineOfBusiness?: string[];
  selectedUnderwriters?: string[];
  selectedUnderwritingYears?: number[];
  selectedStatuses?: string[];
  sortField?: FilterContractsFieldEnum;
  sortOrder?: FilterContractsOrderEnum;
  isFilterOpen: boolean;
  activeTab: EBinderContractTab;
}

// Master Claims Table Filters (Claim Dashboard)
export interface MasterClaimsTableFilters {
  selectedInsureds?: SelectOption[];
  selectedClaimsStatus?: string[];
  selectedLineOfBusiness?: string[];
  selectedReportedDateRange?: [Date, Date];
  selectedClaimsHandler?: string[];
  sortField?: FilterClaimsFieldEnum;
  sortOrder?: FilterClaimsOrderEnum;
  isFilterOpen: boolean;
}

interface DashboardFiltersState {
  binderTable: BinderTableFilters;
  submissionTable: SubmissionTableFilters;
  premiumDueTable: PremiumDueTableFilters;
  binderContractTable: BinderContractTableFilters;
  masterClaimsTable: MasterClaimsTableFilters;

  // Actions for Binder Table
  setBinderTableFilters: (filters: Partial<BinderTableFilters>) => void;
  resetBinderTableFilters: () => void;

  // Actions for Submission Table
  setSubmissionTableFilters: (filters: Partial<SubmissionTableFilters>) => void;
  resetSubmissionTableFilters: () => void;

  // Actions for Premium Due Table
  setPremiumDueTableFilters: (filters: Partial<PremiumDueTableFilters>) => void;
  resetPremiumDueTableFilters: () => void;

  // Actions for Binder Contract Table
  setBinderContractTableFilters: (
    filters: Partial<BinderContractTableFilters>
  ) => void;
  resetBinderContractTableFilters: () => void;

  // Actions for Master Claims Table
  setMasterClaimsTableFilters: (
    filters: Partial<MasterClaimsTableFilters>
  ) => void;
  resetMasterClaimsTableFilters: () => void;

  // Reset all dashboard filters
  resetAllDashboardFilters: () => void;
}

// Default values
const defaultBinderTableFilters: BinderTableFilters = {
  searchText: undefined,
};

const defaultSubmissionTableFilters: SubmissionTableFilters = {
  insureds: undefined,
  transactionTypes: undefined,
  assignedHandlerIds: undefined,
  submissionStatuses: undefined,
  sortField: undefined,
  sortOrder: undefined,
  isFilterOpen: true,
};

const defaultPremiumDueTableFilters: PremiumDueTableFilters = {
  selectedInsureds: undefined,
  selectedReinsureds: undefined,
  selectedBrokers: undefined,
  selectedBinders: undefined,
  selectedPremiumStatuses: undefined,
  selectedPremiumDueDateRange: undefined,
  selectedClosing: undefined,
  sortField: undefined,
  sortOrder: undefined,
  isFilterOpen: true,
};

const defaultBinderContractTableFilters: BinderContractTableFilters = {
  searchPinnacleRefNo: undefined,
  searchLegacyPinRefNo: undefined,
  selectedInsureds: undefined,
  selectedReinsureds: undefined,
  selectedBrokers: undefined,
  selectedBinders: undefined,
  selectedLineOfBusiness: undefined,
  selectedUnderwriters: undefined,
  selectedUnderwritingYears: undefined,
  selectedStatuses: undefined,
  sortField: undefined,
  sortOrder: undefined,
  isFilterOpen: true,
  activeTab: EBinderContractTab.ALL_CONTRACT,
};

const defaultMasterClaimsTableFilters: MasterClaimsTableFilters = {
  selectedInsureds: undefined,
  selectedClaimsStatus: undefined,
  selectedLineOfBusiness: undefined,
  selectedReportedDateRange: undefined,
  selectedClaimsHandler: undefined,
  sortField: undefined,
  sortOrder: undefined,
  isFilterOpen: true,
};

export const useDashboardFiltersStore = create<DashboardFiltersState>(
  (set) => ({
    binderTable: defaultBinderTableFilters,
    submissionTable: defaultSubmissionTableFilters,
    premiumDueTable: defaultPremiumDueTableFilters,
    binderContractTable: defaultBinderContractTableFilters,
    masterClaimsTable: defaultMasterClaimsTableFilters,

    // Binder Table Actions
    setBinderTableFilters: (filters) =>
      set((state) => ({
        binderTable: { ...state.binderTable, ...filters },
      })),
    resetBinderTableFilters: () =>
      set({
        binderTable: defaultBinderTableFilters,
      }),

    // Submission Table Actions
    setSubmissionTableFilters: (filters) =>
      set((state) => ({
        submissionTable: { ...state.submissionTable, ...filters },
      })),
    resetSubmissionTableFilters: () =>
      set({
        submissionTable: defaultSubmissionTableFilters,
      }),

    // Premium Due Table Actions
    setPremiumDueTableFilters: (filters) =>
      set((state) => ({
        premiumDueTable: { ...state.premiumDueTable, ...filters },
      })),
    resetPremiumDueTableFilters: () =>
      set({
        premiumDueTable: defaultPremiumDueTableFilters,
      }),

    // Binder Contract Table Actions
    setBinderContractTableFilters: (filters) =>
      set((state) => ({
        binderContractTable: { ...state.binderContractTable, ...filters },
      })),
    resetBinderContractTableFilters: () =>
      set({
        binderContractTable: defaultBinderContractTableFilters,
      }),

    // Master Claims Table Actions
    setMasterClaimsTableFilters: (filters) =>
      set((state) => ({
        masterClaimsTable: { ...state.masterClaimsTable, ...filters },
      })),
    resetMasterClaimsTableFilters: () =>
      set({
        masterClaimsTable: defaultMasterClaimsTableFilters,
      }),

    resetAllDashboardFilters: () =>
      set({
        binderTable: defaultBinderTableFilters,
        submissionTable: defaultSubmissionTableFilters,
        premiumDueTable: defaultPremiumDueTableFilters,
        binderContractTable: defaultBinderContractTableFilters,
        masterClaimsTable: defaultMasterClaimsTableFilters,
      }),
  })
);
